import { For, Show, createSignal } from "solid-js";

import { useCustomRecord, useCustomRecords } from "~/lib/hooks";

type Props = {
  wsKey: () => string;
  // The custom object whose records this picker can choose from.
  customObjectDefinitionId: () => string;
  value: () => string | null;
  onChange: (recordId: string | null, name: string | null) => void;
  selectedName?: () => string | null;
  label?: string;
  placeholder?: string;
  size?: "sm" | "md";
  disabled?: boolean;
};

export function CustomRecordPicker(props: Props) {
  const [query, setQuery] = createSignal("");
  const [open, setOpen] = createSignal(false);

  const records = useCustomRecords(props.wsKey, props.customObjectDefinitionId, () => ({
    q: query() || undefined,
  }));

  // Auto-resolve the selected record's name when the parent didn't pre-supply
  // one (same pattern as other lookup pickers).
  const resolved = useCustomRecord(props.wsKey, props.customObjectDefinitionId, () =>
    props.value(),
  );

  const chipLabel = () => {
    const supplied = props.selectedName?.();
    if (supplied) return supplied;
    if (resolved.data) return resolved.data.display_name || resolved.data.name;
    return props.value()?.slice(0, 14) ?? "Record";
  };

  const size = () => props.size ?? "md";
  const inputCls = () =>
    size() === "sm" ? "input input-bordered input-sm w-full" : "input input-bordered w-full";

  const clear = () => {
    props.onChange(null, null);
    setQuery("");
  };

  const pick = (id: string, display: string) => {
    // We pass the templated label up so the chip renders the same string
    // the user clicked. Callers that store the value as `selectedName`
    // can render it without re-fetching.
    props.onChange(id, display);
    setQuery("");
    setOpen(false);
  };

  return (
    <div class="relative">
      <Show
        when={props.value()}
        fallback={
          <div>
            <input
              type="text"
              class={inputCls()}
              placeholder={props.placeholder ?? "Search records…"}
              aria-label={props.label ?? "Search records"}
              value={query()}
              disabled={props.disabled}
              onInput={(e) => {
                setQuery(e.currentTarget.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => {
                window.setTimeout(() => setOpen(false), 150);
              }}
              autocomplete="off"
            />
            <Show when={open() && query().length > 0}>
              <ul
                role="listbox"
                aria-label="Record matches"
                class="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-lg bg-base-100 shadow-lg border border-base-300"
              >
                <For each={records.data?.items ?? []}>
                  {(r) => {
                    const display = r.display_name || r.name;
                    return (
                      <li>
                        <button
                          type="button"
                          role="option"
                          aria-selected="false"
                          class="w-full text-left px-3 py-2 hover:bg-base-200"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => pick(r.id, display)}
                        >
                          <div class="font-medium">{display}</div>
                        </button>
                      </li>
                    );
                  }}
                </For>
                <Show when={(records.data?.items.length ?? 0) === 0 && !records.isLoading}>
                  <li class="px-3 py-2 text-sm opacity-60">No records match.</li>
                </Show>
                <Show when={records.isLoading}>
                  <li class="px-3 py-2 text-sm opacity-60">Searching…</li>
                </Show>
              </ul>
            </Show>
          </div>
        }
      >
        <div
          class={`badge ${
            size() === "sm" ? "badge-md" : "badge-lg"
          } badge-primary badge-outline gap-2`}
        >
          <span class="truncate max-w-xs">{chipLabel()}</span>
          <button
            type="button"
            class="btn btn-ghost btn-xs btn-circle"
            aria-label="Clear record"
            disabled={props.disabled}
            onClick={clear}
          >
            ✕
          </button>
        </div>
      </Show>
    </div>
  );
}
